import React, { ComponentProps, MutableRefObject, useMemo } from 'react';

import { SalesOrder } from '../../graphql/entities';
import attributeQuery from '../../graphql/queries/sales/SalesAttributeConfigListQuery.graphql';
import customerAttributeQuery from 'graphql/queries/customer/CustomerAttributeConfigListQuery.graphql';
import Formatters from '../../components/grid-addons/formatters';
import { useSalesWorkspaceActions } from '../../actions/sales/SalesOrderWorkspaceAction';
import {
  IWorkspaceActionButtonsProps,
  WorkspaceActionButtons,
} from '../../components/Workspace/WorkspaceActionButtons';
import { useWorkspace } from '../../graphql/hooks/useWorkspace';
import { useAttributesForWorkspace } from '../../components/Workspace/useAttributesForWorkspace';
import Workspace from '../../components/Workspace';
import { Direction, ISalesOrder } from '../../graphql/schema/p4types';
import { Paths } from '../../@types/keyOfNested';
import { IWorkspaceColumnOverride, IWorkspaceProps } from '../../@types/workspaceTypes';

import { useAbstractSalesHeaderColumnOverrides } from '../../utils/abstractSalesHooks';
import BooleanFormatter from '../../components/grid-addons/formatters/BooleanFormatter';
import { useSalesOrderConfig } from '../../utils/FetchConfig';
import { ModalFormatter } from '../../components/grid-addons/formatters/ModalFormatter';
import { IEditableDataGridColumnDef } from '../../@types/editableDataGridTypes';
import fieldMetaData from '../../graphql/schema/fieldMetaData';
import MaxoptraOrderPriorityFormatter from '../../components/grid-addons/formatters/MaxoptraOrderPriorityFormatter';

const LazyCustomerForm: any = React.lazy(() => import('../customer/CustomerForm'));

const SALES_ORDER_COLUMN_OVERRIDES: IWorkspaceColumnOverride = {
  orderNumber: {
    name: 'Order No',
    key: 'orderNumber',
    width: 100,
  },
  orderDate: {
    name: 'Order Date',
    key: 'orderDate',
    formatter: 'date',
    width: 120,
  },
  'customer.name': {
    name: 'Customer Name',
  },
  'customer.headOffice.customerAccountStatus.description': {
    name: 'Head Office Account Status - Description',
  },
  'customer.headOffice.customerAccountStatus.onStop': {
    name: 'Head Office Account Status - On Stop',
  },
  'customer.reference': {
    name: 'Customer Reference',
    formatter: ModalFormatter,
    width: 125,
    options: {
      modalContent: LazyCustomerForm,
      modalProps: { forceReadOnly: true },
      entityId: 'customer.id',
    },
  },
  'customer.onlyInvoiceFullyDispatched': {
    name: 'Invoice Fully Dispatched Only',
    formatter: Formatters.BooleanFormatter,
  },
  goodsTotal: {
    name: 'Goods Total',
    cellClass: 'rightAlign',
  },
  orderType: {
    name: 'Type',
    width: 100,
  },
  salesOrderStatus: {
    name: 'Status',
    sortable: false,
  },
  targetDate: {
    name: 'Expected Delivery Date',
  },
  targetDispatchDate: {
    name: 'Expected Dispatch Date',
  },
  grossGoodsTotal: {
    name: 'Gross Goods Excl VAT',
  },
  allocatedTotalSaleValue: {
    name: 'Allocated Total',
    options: {
      format: { decimals: 2 },
    },
  },
  unallocatedTotalSaleValue: {
    name: 'Unallocated Total',
    options: {
      format: { decimals: 2 },
    },
  },
  packagingVolumeTotal: {
    formatter: Formatters.NumericFormatter,
    options: { format: { decimals: 9 }, suppressTrailingZeros: true, suppressZeroes: true },
  },
  'customer.address.country.description': {
    name: 'Customer Address - Country',
  },
  'deliveryAddress.country.description': {
    name: 'Delivery Address - Country',
  },
  amazonRefunded: {
    formatter: Formatters.AmazonRefundFormatter,
    name: 'Amazon Refund',
  },
  amazonRefundTotal: {
    name: 'Amazon Refund Total',
    formatter: Formatters.NumericFormatter,
    options: {
      format: { decimals: 2 },
    },
  },
  comment: {
    formatter: Formatters.CommentFormatter,
  },
  'attachments.fileName': {
    name: 'Has Attachments',
    sortable: false,
    formatter: Formatters.AttachmentsFormatter,
  },
  attachments: {
    name: 'Attachments',
    sortable: false,
    formatter: Formatters.AttachmentsFormatter,
  },
  profitMargin: {
    totalCalculation: '[baseProfitValue] / [baseNettGoodsTotal] * 100',
  },
  hasMaxoptraOrder: {
    name: 'Maxoptra Order',
    formatter: BooleanFormatter,
  },
  hasLiveMaxoptraOrder: {
    name: 'Live Maxoptra Order',
    formatter: BooleanFormatter,
  },
  windsorFrameworkAtRiskStatus: {
    name: 'Windsor Framework Status',
    formatter: Formatters.EnumFormatter,
    options: fieldMetaData.WindsorFrameworkAtRiskStatus.enumValues,
  },
  maxoptraOrderPriority: {
    name: 'Maxoptra Order Priority',
    formatter: MaxoptraOrderPriorityFormatter,
  },
  nettTotalDispatchedNotInvoiced: {
    name: 'Goods Dispatched Uninvoiced',
    tooltip: 'Does not include carriage',
  },
};

export const SALES_ORDER_FIELD_NAMES: Array<Paths<ISalesOrder>> = [
  'orderNumber',
  'orderDate',
  'customer.reference',
  'customer.name',
  'orderReference',
  'headerTotal',
  'orderType',
  'salesOrderStatus',
];

const BaseSalesOrderWorkspaceActionButtons = (props: IWorkspaceActionButtonsProps) => {
  const [salesWorkspaceActions, modals] = useSalesWorkspaceActions();
  return (
    <>
      {modals}
      <WorkspaceActionButtons {...props} actionButtons={salesWorkspaceActions} />
    </>
  );
};

export const BaseSalesOrderWorkspace = (
  props: IWorkspaceProps<ISalesOrder> & {
    setWorkspaceRef?: MutableRefObject<any>;
    fieldNames?: Array<Paths<ISalesOrder>>;
    fieldNameOverrides?: Array<Paths<ISalesOrder>>;
  }
) => {
  const { fieldNames = [], setWorkspaceRef, ...rest } = props;
  const { entity, columnOverrides = {} as IWorkspaceColumnOverride, predefinedFilters } = rest;
  const { formUrl, type, title, workspace } = entity!;
  const workspacePredefinedFilters = (entity as any).workspacePredefinedFilters;

  const orderConfig = useSalesOrderConfig();
  const acknowledgedDeliveryDateColumn = useMemo<IWorkspaceColumnOverride>(() => {
    if (!orderConfig?.useAcknowledgedDeliveryDate) {
      return {
        acknowledgedDeliveryDate: {
          disabled: true,
        } as IEditableDataGridColumnDef,
      };
    }
    return {} as IWorkspaceColumnOverride;
  }, [orderConfig]);

  const attributeColumns = useAttributesForWorkspace(attributeQuery);
  const customerAttributeColumns = useAttributesForWorkspace(customerAttributeQuery, {
    valuesPath: 'customer.attributeValues',
    columnKey: 'customer.attributeValues.attributeConfig.title',
  });

  const customColumns = useMemo(
    () => ({ ...(props.customColumns || {}), ...attributeColumns, ...customerAttributeColumns }),
    [props.customColumns, attributeColumns, customerAttributeColumns]
  );

  const baseColumnOverrides: IWorkspaceColumnOverride = useMemo(() => {
    return {
      ...SALES_ORDER_COLUMN_OVERRIDES,
      ...columnOverrides,
      ...acknowledgedDeliveryDateColumn,
    };
  }, [columnOverrides, acknowledgedDeliveryDateColumn]);

  const salesOrderColumnOverrides = useAbstractSalesHeaderColumnOverrides(baseColumnOverrides);

  const workspaceConfig = useMemo(() => {
    return { ...props, predefinedFilters: [...(predefinedFilters || []), ...workspacePredefinedFilters] };
  }, [predefinedFilters, props, workspacePredefinedFilters]);

  const workspaceProps = useWorkspace<ISalesOrder>(workspaceConfig, entity!, {
    columns: fieldNames,
    columnOverrides: salesOrderColumnOverrides,
    customColumns: customColumns,
    initialSortColumn: 'orderNumber',
    initialSortDirection: Direction.Desc,
  });

  return (
    <Workspace
      ref={props.setWorkspaceRef}
      entityType={type}
      title={props.title || title}
      formUrl={formUrl}
      defaultQuery={workspace}
      duplicate={true}
      availableFiltersFilter={props.availableFiltersFilter}
      {...(rest as Partial<ComponentProps<typeof Workspace>>)}
      {...workspaceProps}
      columnOverrides={columnOverrides}
      workspaceActionButtonsHOC={BaseSalesOrderWorkspaceActionButtons}
    />
  );
};

const availableFiltersFilter = (column: IEditableDataGridColumnDef) => {
  return !!column?.fieldName && !column.fieldName.includes('orderType');
};

export default (props: Omit<Parameters<typeof BaseSalesOrderWorkspace>[0], 'entity'>) => {
  const columnOverrides = useMemo<IWorkspaceColumnOverride>(
    () => props.columnOverrides ?? ({} as IWorkspaceColumnOverride),
    [props.columnOverrides]
  );

  const fieldNames = props.fieldNames || SALES_ORDER_FIELD_NAMES;

  return (
    <BaseSalesOrderWorkspace
      entity={SalesOrder}
      availableFiltersFilter={availableFiltersFilter}
      {...props}
      viewScreenName={props.viewScreenName ?? 'Sales/Order/Workspace'}
      fieldNames={fieldNames}
      columnOverrides={columnOverrides}
    />
  );
};
